/**
 * Nuevo assessment — Sprint 16 redesign con wizard ISO 19011.
 *
 * 3 pasos:
 *   1. Tipo de auditoría (internal/external/certification) + datos cert
 *   2. Objetivos + alcance + criterios (ISO 19011 §5.4)
 *   3. Selección de plantillas (con flag IMS si aplica)
 *
 * Si audit_type=certification, paso 1 exige organismo certificador y
 * fase. El validation rule del backend bloquea sin cert_phase.
 */
import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppShell } from '../components/AppShell.js';
import { Wand2 } from 'lucide-react';

import { type CategoryGroup, categoryForCode, groupByCategory } from '@assessos/shared';

import { type AuditType, type CertPhase, assessmentsApi } from '../api/assessments.js';
import { type SurveySummary, surveysApi } from '../api/surveys.js';
import { aiErrorMessage, downloadReport, reportsApi } from '../api/reports.js';
import { Badge, Card, EmptyState, LoadingSkeleton, PageHeader } from '../components/ui.js';
import { Button } from '../components/ui/Button.js';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter,
  ModalClose,
} from '../components/ui/Modal.js';

type Step = 1 | 2 | 3;

const AUDIT_TYPE_META: Record<AuditType, { label: string; tone: string; description: string }> = {
  internal: {
    label: 'Auditoría interna',
    tone: 'brand',
    description:
      'Primera parte (1st party). La realiza la propia organización o personal a su cargo. Foco en mejora interna, no genera certificado.',
  },
  external: {
    label: 'Auditoría externa',
    tone: 'amber',
    description:
      'Segunda parte (2nd party). La realiza un cliente, proveedor o parte interesada externa. No genera certificación pero da confianza a la contraparte.',
  },
  certification: {
    label: 'Auditoría de certificación',
    tone: 'emerald',
    description:
      'Tercera parte (3rd party). La realiza un organismo certificador independiente acreditado. Resulta en emisión o mantenimiento de certificado ISO.',
  },
};

const CERT_PHASE_META: Record<CertPhase, string> = {
  stage_1: 'Etapa 1 — revisión documental',
  stage_2: 'Etapa 2 — auditoría de implementación',
  surveillance: 'Auditoría de seguimiento',
  recertification: 'Auditoría de recertificación',
};

export function AssessmentNewPage(): JSX.Element {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { setBackTo } = useAppShell();
  const [step, setStep] = useState<Step>(1);

  // Back button (BB-UX-01)
  useEffect(() => {
    if (!siteId) return;
    setBackTo({ href: `/sites/${siteId}/assessments`, label: 'Histórico' });
    return () => setBackTo(null);
  }, [setBackTo, siteId]);
  const [available, setAvailable] = useState<SurveySummary[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Paso 1
  const [auditType, setAuditType] = useState<AuditType | ''>('');
  const [certBody, setCertBody] = useState('');
  const [certPhase, setCertPhase] = useState<CertPhase | ''>('');

  // Paso 2
  const [objectivesText, setObjectivesText] = useState('');
  const [auditScope, setAuditScope] = useState('');
  const [criteriaText, setCriteriaText] = useState('');
  const [isIntegrated, setIsIntegrated] = useState(false);

  // Sprint Z · ADR-031 — Estado del botón "Generar informe de apertura"
  const [generatingOpening, setGeneratingOpening] = useState(false);
  const [openingError, setOpeningError] = useState<string | null>(null);
  // Identificadores del informe listo — se usan con downloadReport (Bearer + Blob)
  // No almacenamos download_url porque window.open/href no envía el header Authorization → 401.
  const [openingReportData, setOpeningReportData] = useState<{
    assessmentId: string;
    reportId: string;
  } | null>(null);
  const [openingModalOpen, setOpeningModalOpen] = useState(false);
  // Error específico del botón de descarga dentro del modal
  const [openingDownloadError, setOpeningDownloadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    surveysApi
      .list()
      .then((d) => {
        if (!cancelled) setAvailable(d.templates);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Error cargando catálogo');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!siteId || selected.size === 0) return;
    setError(null);
    setSubmitting(true);
    try {
      const surveys = Array.from(selected).map((key) => {
        const [code, ver] = key.split('@');
        return { code: code!, version: Number(ver) };
      });
      const objectives = objectivesText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const criteria = criteriaText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const payload: Parameters<typeof assessmentsApi.create>[0] = {
        site_id: siteId,
        surveys,
        is_integrated: isIntegrated,
      };
      if (notes.trim()) payload.notes = notes.trim();
      if (auditType) payload.audit_type = auditType;
      if (objectives.length > 0) payload.audit_objectives = objectives;
      if (auditScope.trim()) payload.audit_scope = auditScope.trim();
      if (criteria.length > 0) payload.audit_criteria = criteria;
      if (auditType === 'certification') {
        if (certBody.trim()) payload.cert_body = certBody.trim();
        if (certPhase) payload.cert_phase = certPhase;
      }
      const created = await assessmentsApi.create(payload);
      navigate(`/assessments/${created.id}/capture`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error creando assessment');
    } finally {
      setSubmitting(false);
    }
  }

  function toggle(key: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const grouped = useMemo<CategoryGroup<SurveySummary>[]>(() => {
    if (!available) return [];
    const sorted = [...available].sort((a, b) => a.name.localeCompare(b.name));
    return groupByCategory(sorted, (s) => s.code);
  }, [available]);

  function badgeToneFor(code: string): 'brand' | 'emerald' | 'amber' | 'purple' | 'slate' {
    switch (categoryForCode(code)) {
      case 'physical_security':
        return 'brand';
      case 'cybersecurity':
        return 'emerald';
      case 'regulatory_rd':
        return 'amber';
      case 'customs_food':
        return 'purple';
      default:
        return 'slate';
    }
  }

  // Sprint Z · ADR-031 — Genera el informe de apertura creando primero el assessment.
  // Flujo: crea assessment (sin navegar) → encola ai_opening → polling 3s/60s → modal descarga.
  // NOTA: se declara ANTES de canSubmit para permitir ref en el cuerpo de la función;
  // la verificación usa selected.size y auditType directamente (equivalente a canSubmit).
  const onGenerateOpening = useCallback(async () => {
    if (!siteId || selected.size === 0 || !auditType) return;
    setOpeningError(null);
    setGeneratingOpening(true);
    try {
      // Paso 1: Crear el assessment si no existe aún en este wizard
      const surveys = Array.from(selected).map((key) => {
        const [code, ver] = key.split('@');
        return { code: code!, version: Number(ver) };
      });
      const objectives = objectivesText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const criteria = criteriaText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const payload: Parameters<typeof assessmentsApi.create>[0] = {
        site_id: siteId,
        surveys,
        is_integrated: isIntegrated,
      };
      if (notes.trim()) payload.notes = notes.trim();
      if (auditType) payload.audit_type = auditType;
      if (objectives.length > 0) payload.audit_objectives = objectives;
      if (auditScope.trim()) payload.audit_scope = auditScope.trim();
      if (criteria.length > 0) payload.audit_criteria = criteria;
      if (auditType === 'certification') {
        if (certBody.trim()) payload.cert_body = certBody.trim();
        if (certPhase) payload.cert_phase = certPhase;
      }
      const created = await assessmentsApi.create(payload);
      const newAssessmentId = created.id;

      // Paso 2: Encolar la generación del informe de apertura
      await reportsApi.generate(newAssessmentId, 'ai_opening');

      // Paso 3: Polling hasta que el informe esté listo
      let attempts = 0;
      const tick = async (): Promise<void> => {
        attempts += 1;
        try {
          const r = await reportsApi.list(newAssessmentId);
          const openingReport = r.reports.find((x) => x.kind === 'ai_opening');
          if (openingReport) {
            setOpeningReportData({ assessmentId: newAssessmentId, reportId: openingReport.id });
            setOpeningDownloadError(null);
            setOpeningModalOpen(true);
            setGeneratingOpening(false);
            return;
          }
        } catch {
          // Ignorar errores de polling intermedios
        }
        if (attempts >= 20) {
          setGeneratingOpening(false);
          setOpeningError('ai_timeout');
          return;
        }
        setTimeout(() => void tick(), 3000);
      };
      setTimeout(() => void tick(), 3000);
    } catch (err: unknown) {
      setGeneratingOpening(false);
      const status = (err as { status?: number }).status;
      const message = (err as Error).message ?? '';
      if (status === 503 || message.includes('ai_not_configured')) {
        setOpeningError('ai_not_configured');
      } else if (status === 429 || message.includes('ai_rate_limited')) {
        setOpeningError('ai_rate_limited');
      } else {
        setOpeningError('ai_upstream_unavailable');
      }
    }
  }, [
    siteId,
    selected,
    objectivesText,
    criteriaText,
    isIntegrated,
    notes,
    auditType,
    auditScope,
    certBody,
    certPhase,
  ]);

  const canStep2 =
    auditType !== '' &&
    (auditType !== 'certification' || (certBody.trim().length > 0 && certPhase !== ''));
  const canStep3 = canStep2; // objetivos/criterios son opcionales pero se piden
  const canSubmit = canStep3 && selected.size > 0;

  return (
    <main className="mx-auto max-w-5xl px-6 py-8 space-y-6">
      <PageHeader
        eyebrow="NUEVO LEVANTAMIENTO"
        title="Configura la auditoría"
        subtitle="Tres pasos guiados por ISO 19011:2018 §5.4. La metadata queda registrada con el levantamiento y aparece en el reporte final."
      />

      {/* Stepper */}
      <nav aria-label="Pasos" className="flex items-center gap-2">
        {([1, 2, 3] as Step[]).map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => {
              if (n === 1) setStep(1);
              if (n === 2 && canStep2) setStep(2);
              if (n === 3 && canStep3) setStep(3);
            }}
            className={
              'flex-1 rounded-lg border-2 p-3 text-left transition-colors ' +
              (step === n
                ? 'border-brand-700 bg-brand-50'
                : 'border-slate-200 bg-white hover:border-slate-300')
            }
          >
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Paso {n}
            </p>
            <p className="text-sm font-semibold text-brand-900">
              {n === 1 ? 'Tipo de auditoría' : n === 2 ? 'Objetivos y alcance' : 'Plantillas'}
            </p>
          </button>
        ))}
      </nav>

      {error && (
        <div
          role="alert"
          className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700"
        >
          {error}
        </div>
      )}

      <form
        onSubmit={(e) => {
          void submit(e);
        }}
        className="space-y-6"
      >
        {/* ── Paso 1: tipo ───────────────────────────────────────── */}
        {step === 1 && (
          <Card
            title="¿Qué tipo de auditoría vas a iniciar?"
            hint="ISO 19011:2018 §3.1 — clasificación por parte que la ejecuta"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {(Object.keys(AUDIT_TYPE_META) as AuditType[]).map((t) => {
                const meta = AUDIT_TYPE_META[t];
                const active = auditType === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setAuditType(t)}
                    className={
                      'rounded-xl border-2 p-4 text-left transition-colors ' +
                      (active
                        ? 'border-brand-700 bg-brand-50'
                        : 'border-slate-200 bg-white hover:border-slate-300')
                    }
                  >
                    <p className="text-sm font-bold text-brand-900">{meta.label}</p>
                    <p className="mt-2 text-xs text-slate-600 leading-relaxed">
                      {meta.description}
                    </p>
                  </button>
                );
              })}
            </div>

            {auditType === 'certification' && (
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-100 pt-5">
                <div>
                  <label
                    htmlFor="cert_body"
                    className="block text-xs font-semibold text-slate-700 mb-1"
                  >
                    Organismo certificador <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="cert_body"
                    type="text"
                    value={certBody}
                    onChange={(e) => setCertBody(e.target.value)}
                    placeholder="Ej. SGS, BSI, INTECO, DGN/MIC"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="cert_phase"
                    className="block text-xs font-semibold text-slate-700 mb-1"
                  >
                    Fase de certificación <span className="text-rose-500">*</span>
                  </label>
                  <select
                    id="cert_phase"
                    value={certPhase}
                    onChange={(e) => setCertPhase(e.target.value as CertPhase)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">— Selecciona —</option>
                    {(Object.keys(CERT_PHASE_META) as CertPhase[]).map((p) => (
                      <option key={p} value={p}>
                        {CERT_PHASE_META[p]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                disabled={!canStep2}
                onClick={() => setStep(2)}
                className="rounded-lg bg-brand-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700/90 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                Siguiente →
              </button>
            </div>
          </Card>
        )}

        {/* ── Paso 2: objetivos/alcance/criterios ─────────────────── */}
        {step === 2 && (
          <Card
            title="Objetivos, alcance y criterios"
            hint="ISO 19011:2018 §5.4 — determinar y comunicar antes de la auditoría"
          >
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="audit_objectives"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  Objetivos (uno por línea)
                </label>
                <textarea
                  id="audit_objectives"
                  rows={4}
                  value={objectivesText}
                  onChange={(e) => setObjectivesText(e.target.value)}
                  placeholder="Ej.&#10;Verificar conformidad con ISO 27001:2022&#10;Evaluar efectividad del sistema de gestión&#10;Identificar oportunidades de mejora"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Recomendación 19011: objetivos medibles, claros y compartidos con la parte
                  auditada.
                </p>
              </div>

              <div>
                <label
                  htmlFor="audit_scope"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  Alcance
                </label>
                <textarea
                  id="audit_scope"
                  rows={3}
                  value={auditScope}
                  onChange={(e) => setAuditScope(e.target.value)}
                  placeholder="Sedes, procesos, períodos, exclusiones aplicables…"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label
                  htmlFor="audit_criteria"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  Criterios (uno por línea)
                </label>
                <textarea
                  id="audit_criteria"
                  rows={4}
                  value={criteriaText}
                  onChange={(e) => setCriteriaText(e.target.value)}
                  placeholder="Ej.&#10;ISO/IEC 27001:2022&#10;Política interna PO-SEG-001 v3&#10;Resolución JM RD seguridad cibernética"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Normas, políticas internas, regulaciones aplicables contra las que se mide
                  conformidad.
                </p>
              </div>

              <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isIntegrated}
                  onChange={(e) => setIsIntegrated(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-500/30"
                />
                <div>
                  <p className="text-sm font-semibold text-brand-900">
                    Auditoría de sistemas integrados (IMS)
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Activa cuando aplicas múltiples normas a la vez (ej. ISO 27001 + ISO 9001).
                    Habilita la matriz Annex SL para cruzar evidencia entre normas con cláusulas
                    compartidas.
                  </p>
                </div>
              </label>
            </div>

            <div className="mt-5 flex justify-between">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                ← Anterior
              </button>
              <button
                type="button"
                disabled={!canStep3}
                onClick={() => setStep(3)}
                className="rounded-lg bg-brand-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700/90 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                Siguiente →
              </button>
            </div>
          </Card>
        )}

        {/* ── Paso 3: plantillas ──────────────────────────────────── */}
        {step === 3 && (
          <>
            {available === null && <LoadingSkeleton rows={4} />}

            {available !== null && available.length === 0 && (
              <EmptyState
                title="No hay plantillas publicadas"
                description="Pídele a un admin que cree o publique una plantilla en /admin/surveys."
              />
            )}

            {grouped.map((cat) => (
              <Card
                key={cat.category}
                title={cat.meta.label}
                hint={`${cat.items.length} ${cat.items.length === 1 ? 'plantilla' : 'plantillas'} · ${cat.meta.description}`}
              >
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {cat.items.map((t) => {
                    const key = `${t.code}@${String(t.version)}`;
                    const isChecked = selected.has(key);
                    return (
                      <li key={key}>
                        <label
                          className={
                            'flex cursor-pointer items-start gap-3 rounded-xl border-2 p-4 transition-colors ' +
                            (isChecked
                              ? 'border-brand-700 bg-brand-50'
                              : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50')
                          }
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggle(key)}
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-500/30"
                          />
                          <div className="flex-1 min-w-0 space-y-1">
                            <p className="text-sm font-semibold text-brand-900 leading-snug">
                              {t.name}
                            </p>
                            <p className="text-xs font-mono text-slate-400">
                              {t.code} · v{t.version}
                            </p>
                            <div className="flex flex-wrap gap-1.5 pt-1">
                              <Badge tone={badgeToneFor(t.code)}>{cat.meta.short}</Badge>
                            </div>
                          </div>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </Card>
            ))}

            <div className="space-y-2">
              <label htmlFor="notes" className="block text-xs font-semibold text-slate-700">
                Notas del levantamiento (opcional)
              </label>
              <textarea
                id="notes"
                rows={2}
                placeholder="Ej. Recertificación anual · auditor principal · ventana 14:00-18:00"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>

            {/* Sprint Z · ADR-031 — Error inline del botón IA (role="alert" WCAG 4.1.3) */}
            {openingError && (
              <div
                role="alert"
                className="rounded bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700"
              >
                {aiErrorMessage(openingError)}
              </div>
            )}

            {/* Sticky CTA bar */}
            <div className="sticky bottom-0 z-10 -mx-6 border-t border-slate-200 bg-white px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-sm text-slate-600">
                <strong className="font-bold text-brand-900">
                  {selected.size} {selected.size === 1 ? 'plantilla' : 'plantillas'}
                </strong>{' '}
                seleccionada{selected.size === 1 ? '' : 's'}
                {auditType && (
                  <span className="ml-2 text-xs text-slate-400">
                    · {AUDIT_TYPE_META[auditType].label}
                    {isIntegrated && ' · IMS'}
                  </span>
                )}
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 w-full sm:w-auto"
                >
                  ← Anterior
                </button>
                {/* Sprint Z · ADR-031 — Botón "Generar informe de apertura" */}
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  disabled={!canSubmit || generatingOpening}
                  loading={generatingOpening}
                  loadingText="Generando informe…"
                  aria-busy={generatingOpening}
                  aria-label={
                    generatingOpening
                      ? 'Generando informe de apertura con IA, espera…'
                      : 'Generar informe de apertura con IA'
                  }
                  title={
                    !canSubmit
                      ? 'Completa el tipo de auditoría y selecciona al menos una plantilla'
                      : undefined
                  }
                  onClick={() => void onGenerateOpening()}
                  className="w-full sm:w-auto"
                >
                  <Wand2 className="h-4 w-4" aria-hidden="true" />
                  {generatingOpening ? 'Generando…' : 'Generar informe de apertura'}
                </Button>
                <button
                  type="submit"
                  disabled={submitting || !canSubmit}
                  className="rounded-lg bg-brand-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700/90 disabled:cursor-not-allowed disabled:bg-slate-300 w-full sm:w-auto"
                >
                  {submitting ? 'Creando…' : `Iniciar levantamiento →`}
                </button>
              </div>
            </div>

            {/* Modal de resultado del informe de apertura */}
            <Modal
              open={openingModalOpen}
              onOpenChange={(open) => {
                setOpeningModalOpen(open);
                if (!open) setOpeningDownloadError(null);
              }}
            >
              <ModalContent size="md">
                <ModalHeader>
                  <ModalTitle>Informe de apertura generado</ModalTitle>
                  <ModalDescription>
                    El acta de apertura ha sido generada con IA. Descárgala y compártela con el
                    equipo auditado.
                  </ModalDescription>
                </ModalHeader>
                <div className="py-4 space-y-3">
                  <p className="text-sm text-slate-600">
                    El levantamiento ha sido creado. Puedes iniciar la captura cuando estés listo.
                  </p>
                  {openingDownloadError && (
                    <div
                      role="alert"
                      className="rounded bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700"
                    >
                      {aiErrorMessage(openingDownloadError)}
                    </div>
                  )}
                </div>
                <ModalFooter>
                  <ModalClose asChild>
                    <Button variant="secondary" size="sm">
                      Cerrar
                    </Button>
                  </ModalClose>
                  {openingReportData && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setOpeningDownloadError(null);
                        downloadReport(
                          openingReportData.assessmentId,
                          openingReportData.reportId,
                          'ai_opening',
                        ).catch((err: unknown) => {
                          const status = (err as { status?: number }).status;
                          const message = (err as Error).message ?? '';
                          if (status === 429 || message.includes('ai_rate_limited')) {
                            setOpeningDownloadError('ai_rate_limited');
                          } else {
                            setOpeningDownloadError('download_failed');
                          }
                        });
                      }}
                    >
                      Descargar informe
                    </Button>
                  )}
                </ModalFooter>
              </ModalContent>
            </Modal>
          </>
        )}
      </form>
    </main>
  );
}
